import { SqlDbDefinitionMap, SqlTableDefinition } from "@cervval.js/common-sql-core/src/sql.common";
import { getForeignKeyTableName } from "@cervval.js/common-sql-core/src/sql.tableDefinition.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { TEMPORARY_ID_SERVICE, TemporaryIdService } from "@cervval.js/common/src/services/temporaryIds.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { CacheOwner, Cached, destroyObject } from "@cervval.js/common/src/tools/cache.tools";
import { CommonEvent, CommonEventHandler } from "@cervval.js/common/src/tools/event.tools";
import { ActionPersistResult } from "../../action.model";
import { DTOCacheRepository } from "../../dtos/dtoCacheRepository";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "../cache.service";

/**
 * Default Action Cache Service
 * Contains a map with all the DTO Cache Repositories
 * 
 * ! This service should not be injected server side since cache must not be shared between user sessions !
 * On server, you must use a different cache service each time
 * 
 */
@Injectable(ACTION_CACHE_SERVICE)
export class DefaultActionCacheService<ElementTypes, CacheInfo = unknown> implements ActionCacheService<ElementTypes>, CacheOwner {

    @Injected(LOG_SERVICE)
    public readonly log!: LogService;

    @Injected(TEMPORARY_ID_SERVICE)
    public readonly temporaryIdService!: TemporaryIdService;

    public cacheData: Map<string, unknown> = new Map();

    /**
     * Map with the type name as key and the cache repository as value
     */
    protected _dtoCacheRepositoryMap = new Map<keyof ElementTypes, DTOCacheRepository<unknown, CacheInfo>>();

    /** map referencing an element table definition */
    protected _elementTypeTableDefinitionMap: SqlDbDefinitionMap<keyof ElementTypes, string>;

    /** once disposed this boolean is set to true */
    protected _disposed: boolean = false;

    constructor(_elementTypeTableDefinitionMap: SqlDbDefinitionMap<keyof ElementTypes, string>) {
        this._elementTypeTableDefinitionMap = _elementTypeTableDefinitionMap;
        this._installCacheDirtyHandler();
    }

    /** 
     * @inheritdoc
     */
    public dispose(p: { disposeRepositories: boolean }): void {
        if (this._disposed) { return; }
        if (p.disposeRepositories) {
            for (const repo of this._dtoCacheRepositoryMap.values()) {
                repo.dispose();
            }
        }
        destroyObject(this);
        this._disposed = true;
    }

    /**
     * Mark dirty all repositories on cacheDirty event
     */
    protected _installCacheDirtyHandler(): void {
        this.cacheDirty.on(() => {
            for (const repo of this._dtoCacheRepositoryMap.values()) {
                repo.markDirty();
            }
            this.dirtyStateChanged.emit();
        });
    }

    /**
     * Get the table definition map used in cache
     * @returns {Map<keyof ElementTypes, SqlTableDefinition>}
     */
    public getTableDefinitionMap(): Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>> {
        return this._elementTypeTableDefinitionMap;
    }

    /**
     * @param elementTypeName 
     * @returns {SqlTableDefinition<string, string>} sql table definition of given element
     */
    public getTableDefinition(elementTypeName: keyof ElementTypes): SqlTableDefinition<keyof ElementTypes, string> | undefined {
        return this._elementTypeTableDefinitionMap.get(elementTypeName);
    }

    /** 
     * @inheritdoc
     * Warning: Result is cached. It will not work with dynamic foreign tables.
     * MAB: I would suggest removing the cache or creating a new specific Cache Service when this will happen.
     */
    @Cached()
    public getForeignKeyTableName<ET extends keyof ElementTypes>(elementTypeName: ET, fieldName: keyof ElementTypes[ET]): keyof ElementTypes | null {
        return getForeignKeyTableName(this._elementTypeTableDefinitionMap, elementTypeName, fieldName);
    }

    /**
     * Registers a DTOCacheRepository with default options for all given type names
     */
    public registerDTOCacheRepositories<ElementTypeName extends keyof ElementTypes>(elementTypeNames: ElementTypeName[]): void {
        for (const elementTypeName of elementTypeNames) {
            const dtoCacheRepository = new DTOCacheRepository<unknown, CacheInfo>({ elementTypeName: String(elementTypeName) });
            this.registerDTOCacheRepository(elementTypeName, dtoCacheRepository);
        }
    }

    /**
     * Associate the cache repository to the provided elementTypeName
     */
    public registerDTOCacheRepository<ElementTypeName extends keyof ElementTypes, ElementType extends ElementTypes[ElementTypeName]>(elementTypeName: ElementTypeName, dtoCacheRepository: DTOCacheRepository<ElementType | unknown, CacheInfo>): void {
        if (this._dtoCacheRepositoryMap.has(elementTypeName)) {
            this.log.error(`a cache repository is already registered for ${String(elementTypeName)}`);
        }
        if (elementTypeName !== dtoCacheRepository.elementTypeName) {
            this.log.warn(`registering ${String(dtoCacheRepository.elementTypeName)} as ${String(elementTypeName)}`);
        }
        this._dtoCacheRepositoryMap.set(elementTypeName, dtoCacheRepository);
    }

    /**
     * Get the DTOCacheRepository for a given type name
     * @param elementTypeName 
     * @returns {DTOCacheRepository} or throws an exception
     */
    public getDTOCacheRepository<ElementTypeName extends keyof ElementTypes, ElementType extends ElementTypes[ElementTypeName]>(elementTypeName: ElementTypeName): DTOCacheRepository<ElementType, CacheInfo> {
        const dtoCacheRepository = this._dtoCacheRepositoryMap.get(elementTypeName);
        if (dtoCacheRepository) {
            return dtoCacheRepository as unknown as DTOCacheRepository<ElementType, CacheInfo>;
        } else {
            throw new Error(`DTOCacheRepository not found for type ${String(elementTypeName)}`);
        }
    }

    /**
     * Find the DTOCacheRepository for a given type name
     * @param elementTypeName 
     * @returns {DTOCacheRepository} or undefined
     */
    public findDTOCacheRepository<ElementTypeName extends keyof ElementTypes, ElementType extends ElementTypes[ElementTypeName]>(elementTypeName: ElementTypeName): DTOCacheRepository<ElementType, CacheInfo> | undefined {
        const dtoCacheRepository = this._dtoCacheRepositoryMap.get(elementTypeName);
        if (dtoCacheRepository) {
            return dtoCacheRepository as unknown as DTOCacheRepository<ElementType, CacheInfo>;
        } else {
            return undefined;
        }
    }

    /**
     * Set a temporary id on an element
     * @param elementTypeName 
     * @param elementToPrepare 
     */
    public setTemporaryId<ElementTypeName extends keyof ElementTypes, ElementType extends ElementTypes[ElementTypeName]>(elementTypeName: ElementTypeName, elementToPrepare: ElementType): void {
        const cacheRepository = this.getDTOCacheRepository(elementTypeName);
        const nextId = this.temporaryIdService.getNextId(`${String(elementTypeName)}`);
        cacheRepository.setId(elementToPrepare, nextId);
    }

    /**
     * Update cache using action persistence server results
     * @param actionPersistResult 
     */
    public updatePersistedElements<ElementTypeName extends keyof ElementTypes>(actionPersistResult: ActionPersistResult): void {

        // update ids
        Object.keys(actionPersistResult.updatedIds).forEach(elementTypeName => {
            const cacheRepository = this.getDTOCacheRepository(elementTypeName as ElementTypeName);
            const updatedIds = actionPersistResult.updatedIds[elementTypeName];
            Object.keys(updatedIds).forEach(oldId => cacheRepository.changeId(+oldId, getProp(updatedIds, oldId)));
        });

        // update foreign key ids
        actionPersistResult.updatedForeignKeys?.forEach(updatedForeignKey => {
            this.getDTOCacheRepository(updatedForeignKey.elementTypeName as ElementTypeName).changeForeignKeyId(updatedForeignKey);
        });

    }

    /** Triggered when the whole cache needs to be tagged dirty (usually on persistenceError) */
    @CommonEvent()
    public cacheDirty!: CommonEventHandler<{ reason: "persistenceError" | string }>;

    /** 
     * Triggered when the "refresh button" state needs to be changed (usually after websocket message or persistenceError)
     */
    @CommonEvent()
    public dirtyStateChanged!: CommonEventHandler<void>;
}
